import { useEffect } from 'react';
import { PageFeed } from 'model/indexTS';
import ClassNames from '@streamloots/classnames';
import { useTranslation } from 'utils/translation';
import trackEventManager from 'utils/event-manager';
import {
  DEFAULT_PAGE_FEED_COMMENTS_LIMIT,
  usePageFeedCommentActions,
  usePageFeedCommentSelectors,
} from 'services/page-feed-comments';
import { usePageSelectors } from 'services/page';
import Skeleton from 'ui-library/skeleton';
import { FetcherPagination } from 'components/fetcher-pagination';
import { canFetchMoreItems } from 'components/fetcher';
import { FeedComment } from './FeedComment';
import { AddComment } from './AddComment';
import theme from './feed-comments.scss';
import { FeedCommentContext } from './useFeedComments';

const classNames = ClassNames(theme);

export interface FeedCommentsListProps {
  feed: PageFeed;
}

export const FeedCommentsList = ({ feed }: FeedCommentsListProps): JSX.Element | null => {
  const { page } = usePageSelectors();
  const { data: feedComments, pagination, query, isFetchingNextItems } = usePageFeedCommentSelectors(feed._id);
  const { fetchPageFeedComments, fetchPageFeedNextComments } = usePageFeedCommentActions();
  const { t } = useTranslation('pageFeed');

  useEffect(() => {
    fetchPageFeedComments({ feedId: feed._id, limit: DEFAULT_PAGE_FEED_COMMENTS_LIMIT, slug: page.slug });
  }, [feed._id, fetchPageFeedComments, page.slug]);

  const moreItems = (): boolean => {
    return canFetchMoreItems({ data: feedComments, pagination, query });
  };

  const handleShowMoreClick = () => {
    if (!pagination?.next) {
      return;
    }

    trackEventManager.feedClicked({
      button: 'load more comments',
      location: 'feed content',
      content_id: feed._id,
      like_count: feed.summary.reactionCount,
      comment_count: feed.summary.commentCount,
    });

    fetchPageFeedNextComments(feed._id, pagination.next);
  };

  if (!feedComments) {
    return <Skeleton dataTestId="feedCommentSkeleton" height={100} />;
  }

  return (
    <FeedCommentContext.Provider value={{ feed }}>
      <>
        <div className={classNames('feed-comments-list')}>
          {feedComments.map(feedComment => (
            <FeedComment feedComment={feedComment} key={feedComment._id} t={t} />
          ))}
        </div>

        <FetcherPagination
          moreItems={moreItems}
          fetchNext={handleShowMoreClick}
          isLoading={isFetchingNextItems}
          label={t('pageFeed:feedDetail.comments.showMore')}
          className={classNames('feed-comments__more')}
          buttonClassName={classNames('feed-comments__more__button')}
          separatorClassName={classNames('feed-comments__more__hide')}
          iconClassNames={classNames('feed-comments__more__hide')}
          buttonProps={{ ariaLabel: t('pageFeed:feedDetail.comments.showMore') }}
        />
        <AddComment t={t} />
      </>
    </FeedCommentContext.Provider>
  );
};
